import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { FormActionsNew } from "@/features/FormCard/Actions";
import { InputField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

const client = "etl-quantilia";
const table_name = "connector_configs";

const FormSchema = z.object({
  protocol: z.string().nullable().optional(),
  connector: z.string().nullable().optional(),
  path: z.string().nullable().optional(),
  method: z.string().nullable().optional(),
  unzip_strategy: z.string().nullable().optional(),
  file_pattern: z.string().nullable().optional(),
  email_source_type: z.string().nullable().optional(),
  email_subject_user: z.string().nullable().optional(),
});

export function ConnectorConfigsNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Connector Config
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField name="protocol" label="Protocol" control={form.control} />
                  <InputField name="connector" label="Connector" control={form.control} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField name="path" label="Path" control={form.control} />
                  <InputField name="method" label="Method" control={form.control} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="file_pattern"
                    label="File Pattern"
                    control={form.control}
                  />
                  <InputField
                    name="unzip_strategy"
                    label="Unzip Strategy"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="email_source_type"
                    label="Email Source Type"
                    control={form.control}
                  />
                  <InputField
                    name="email_subject_user"
                    label="Email Subject User"
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew onReset={handleReset} onDelete={openDeleteModal} />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
